import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useCopy, useFramework } from "../framework";
import { iconFor } from "../components/icons";
import { ArrowLinkLabel, EmptyState, PageHeader, SectionHeading, Trail, ordinal } from "../components/ui";
import { useScrollToHash } from "./support";

const SearchIcon = iconFor("search");

/**
 * The language page. Every string resolves through the locale pack, so a partner
 * configuration overrides this page the same way it overrides any other.
 *
 * copy-register: commercial
 *
 * Content contract: docs/content/product-marketing-foundation.md is the canonical
 * brief for shipped copy. This page is a writing guide, not a set of commitments:
 * the owner overrode the earlier ruling on 12 August 2026, because a reader could
 * not produce a sentence from a page that only described its own rules. Every
 * section therefore ends in strings: the rule, the line to write, the line that
 * fails, and one reason why. The refusals and the twelve patterns carry the same
 * triad, because a rule with no string beside it is the failure this page was
 * rebuilt to remove.
 *
 * Two consequences are deliberate. The page carries no benchmark figure from
 * another organisation, because §6 rules that out and because a metric name is
 * the writing instruction, not somebody else's number; example record numbers and
 * amounts are labelled as examples. And it names the words it refuses by the
 * shape they take, not by printing the retired vocabulary the same §2 bans from
 * shipped surfaces.
 *
 * The audience craft and the jobs-to-be-done sections ship on the Accenture
 * site: writing rules are not pursuit material. The role research behind them
 * (fears, switching forces) stays in internal projections as a note under each
 * family.
 *
 * This is a reading surface, so it declares no statement plane, as the glossary
 * and the questions page do not (packages/design-system/DESIGN.md).
 */

/** Section anchors, shared with the search index so a result lands on the section it names. */
export interface LanguageSection {
  id: string;
  headingKey: string;
  descriptionKey: string;
  /** Terms a reader may search for that the section heading does not use. Never rendered. */
  keywordsKey: string;
}

export const languageSections: LanguageSection[] = [
  {
    id: "registers",
    headingKey: "page.language.registers.heading",
    descriptionKey: "page.language.registers.description",
    keywordsKey: "page.language.registers.keywords"
  },
  {
    id: "audiences",
    headingKey: "page.language.audiences.heading",
    descriptionKey: "page.language.audiences.description",
    keywordsKey: "page.language.audiences.keywords"
  },
  {
    id: "jobs",
    headingKey: "page.language.jobs.heading",
    descriptionKey: "page.language.jobs.description",
    keywordsKey: "page.language.jobs.keywords"
  },
  {
    id: "mechanism",
    headingKey: "page.language.mechanism.heading",
    descriptionKey: "page.language.mechanism.description",
    keywordsKey: "page.language.mechanism.keywords"
  },
  {
    id: "vocabulary",
    headingKey: "page.language.vocabulary.heading",
    descriptionKey: "page.language.vocabulary.description",
    keywordsKey: "page.language.vocabulary.keywords"
  },
  {
    id: "terms",
    headingKey: "page.language.terms.heading",
    descriptionKey: "page.language.terms.description",
    keywordsKey: "page.language.terms.keywords"
  },
  {
    id: "refusals",
    headingKey: "page.language.refusals.heading",
    descriptionKey: "page.language.refusals.description",
    keywordsKey: "page.language.refusals.keywords"
  },
  {
    id: "patterns",
    headingKey: "page.language.patterns.heading",
    descriptionKey: "page.language.patterns.description",
    keywordsKey: "page.language.patterns.keywords"
  },
  {
    id: "glossary",
    headingKey: "page.glossary.title",
    descriptionKey: "page.glossary.description",
    keywordsKey: "page.language.glossary.keywords"
  }
];

const sectionById = new Map(languageSections.map((section) => [section.id, section]));

const registers = ["framework", "app"] as const;
const audiences = ["leadership", "category", "operations", "finance", "requesters", "suppliers", "technology"] as const;
export type LanguageAudience = (typeof audiences)[number];
const jobParts = ["verb", "unit", "endpoint", "hardness"] as const;
/** The two job statements shown are the shipped strings, resolved from the Workblock that owns them. */
const jobExamples = [
  { id: "invoice", workblockId: "invoice-exception-triage" },
  { id: "savings", workblockId: "savings-initiative" }
] as const;
const mechanismPairs = ["agenda", "sourcing"] as const;
const terms = ["workblock", "system-of-work", "agent", "proposal", "exception", "rails"] as const;
const buyerTerms = [
  "addressable-spend",
  "spend-under-management",
  "off-contract-spend",
  "category-strategy",
  "sourcing-event",
  "should-cost",
  "bid-tabulation",
  "playbook",
  "redline-turn",
  "obligation",
  "evergreen",
  "supplier-onboarding",
  "guided-buying",
  "doa",
  "three-way-match",
  "match-tolerance",
  "goods-receipt",
  "touchless",
  "first-time-match",
  "baseline",
  "savings-stages",
  "savings-realisation",
  "value-leakage",
  "contract-compliance",
  "dpo",
  "cost-per-invoice"
] as const;
const differentiators = ["coverage", "realisation", "cycle-time", "touchless", "compliance"] as const;
const refusals = ["authority", "state", "blocked"] as const;
const patterns = [
  "decision",
  "ceiling",
  "edit",
  "derivation",
  "denominator",
  "gap",
  "redesign",
  "accountability",
  "judgement",
  "reversal",
  "sequence",
  "consistency"
] as const;

/**
 * The glossary, carried as the closing section of this page: the words the
 * platform uses belong beside the rules for using them. /glossary resolves here.
 */
function GlossarySection() {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const [query, setQuery] = useState("");
  const entries = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return snapshot.content.glossary;
    return snapshot.content.glossary.filter((entry) =>
      `${copy(entry.termKey)} ${copy(entry.definitionKey)}`.toLowerCase().includes(normalized)
    );
  }, [query, snapshot, copy]);

  return (
    <section className="apf-page-section apf-glossary-section" id="glossary" aria-labelledby="glossary-title">
      <div className="apf-section-heading">
        <div>
          <h2 id="glossary-title">{copy("page.glossary.title")}</h2>
          <p>{copy("page.glossary.description")}</p>
        </div>
        <label className="apf-search-field">
          <SearchIcon size={16} />
          <span className="apf-visually-hidden">{copy("page.language.glossary.search.label", "Search glossary")}</span>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={copy("page.language.glossary.search.placeholder", "Search terms and definitions")}
          />
        </label>
      </div>
      <div className="apf-glossary-list">
        {entries.map((entry) => (
          <article className="apf-glossary-entry" id={`glossary-${entry.id}`} key={entry.id}>
            <h3>{copy(entry.termKey)}</h3>
            <p>{copy(entry.definitionKey)}</p>
          </article>
        ))}
      </div>
      {!entries.length ? (
        <EmptyState
          title={copy("page.language.glossary.empty.title", "No glossary terms match")}
          description={copy("page.language.glossary.empty.description", "Try a broader term or clear the search.")}
        />
      ) : null}
    </section>
  );
}

export interface LanguagePageProps {
  /** Optional audience-owned research rendered after the shared writing rules. */
  renderAudienceNote?: (audience: LanguageAudience) => ReactNode;
  /** Internal surfaces show the writing craft (worked/broken pairs, writing
   * rules, the job grammar, the mechanism exercises). The published site reads what
   * the platform says and what its words mean, never the authoring
   * instructions: the craft narrates the governing. */
  showCraft?: boolean;
}

export function LanguagePage({ renderAudienceNote, showCraft = false }: LanguagePageProps = {}) {
  const copy = useCopy();
  const { snapshot } = useFramework();
  useScrollToHash();
  const heading = (id: string) => copy(sectionById.get(id)?.headingKey ?? id);
  const description = (id: string) => copy(sectionById.get(id)?.descriptionKey ?? id);

  /** Worked string, the version that fails, and the one line that separates them. */
  const workedPair = (keyPrefix: string, termKey?: string) => (
    <div className="apf-language-pair">
      {termKey ? <p className="apf-language-pair__term">{copy(termKey)}</p> : null}
      <div>
        <p className="apf-language-pair__label">{copy("page.language.label.worked")}</p>
        <p className="apf-language-pair__line apf-language-pair__line--kept">{copy(`${keyPrefix}.worked`)}</p>
      </div>
      <div>
        <p className="apf-language-pair__label">{copy("page.language.label.broken")}</p>
        <p className="apf-language-pair__line">{copy(`${keyPrefix}.broken`)}</p>
      </div>
      <p className="apf-language-pair__note">
        <span>{copy("page.language.label.why")}</span> {copy(`${keyPrefix}.why`)}
      </p>
    </div>
  );

  const businessModuleById = new Map(snapshot.businessModuleRegistry.businessModules.map((module) => [module.id, module]));

  return (
    <div className="apf-page apf-register-reference apf-language-page">
      <PageHeader
        eyebrow={copy("page.language.eyebrow")}
        title={copy("page.language.title")}
        description={copy("page.language.description")}
      />

      <section className="apf-page-section" id="registers">
        <SectionHeading title={heading("registers")} description={description("registers")} />
        <div className="apf-ruled-columns">
          {registers.map((register) => (
            <article className="apf-ruled-columns__item" key={register}>
              <p className="apf-ruled-columns__eyebrow">{copy(`page.language.registers.${register}.eyebrow`)}</p>
              <h2>{copy(`page.language.registers.${register}.headline`)}</h2>
              <p>{copy(`page.language.registers.${register}.body`)}</p>
            </article>
          ))}
        </div>
        {showCraft ? (
          <div className="apf-language-pairs">
            {registers.map((register) => (
              <div key={register}>
                {workedPair(`page.language.registers.${register}`, `page.language.registers.${register}.eyebrow`)}
              </div>
            ))}
          </div>
        ) : null}
      </section>

      <section className="apf-page-section" id="audiences">
        <SectionHeading title={heading("audiences")} description={description("audiences")} />
        <ol className="apf-ruled-index">
          {audiences.map((audience, index) => (
            <li className="apf-ruled-index__row" key={audience}>
              <div className="apf-ruled-index__body">
                <span className="apf-ruled-index__ordinal">{ordinal(index + 1)}</span>
                <div className="apf-ruled-index__heading">
                  <strong className="apf-ruled-index__name">{copy(`page.language.audience.${audience}.label`)}</strong>
                </div>
                <p className="apf-ruled-index__description">{copy(`page.language.audience.${audience}.job`)}</p>
                <div className="apf-language-craft">
                  <div>
                    <p className="apf-language-craft__label">{copy("page.language.label.measured")}</p>
                    <p className="apf-language-craft__line">{copy(`page.language.audience.${audience}.measured`)}</p>
                  </div>
                  {showCraft ? (
                    <div>
                      <p className="apf-language-craft__label">{copy("page.language.label.rules")}</p>
                      <ol className="apf-language-craft__rules">
                        <li>{copy(`page.language.audience.${audience}.rule.one`)}</li>
                        <li>{copy(`page.language.audience.${audience}.rule.two`)}</li>
                      </ol>
                    </div>
                  ) : null}
                  {renderAudienceNote?.(audience)}
                  {showCraft ? <div className="apf-language-pairs">{workedPair(`page.language.audience.${audience}`)}</div> : null}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {showCraft ? (
      <section className="apf-page-section" id="jobs">
        <SectionHeading title={heading("jobs")} description={description("jobs")} />
        <ol className="apf-ruled-index">
          {jobParts.map((part, index) => (
            <li className="apf-ruled-index__row" key={part}>
              <div className="apf-ruled-index__body">
                <span className="apf-ruled-index__ordinal">{ordinal(index + 1)}</span>
                <div className="apf-ruled-index__heading">
                  <strong className="apf-ruled-index__name">{copy(`page.language.jobs.part.${part}.label`)}</strong>
                </div>
                <p className="apf-ruled-index__description">{copy(`page.language.jobs.part.${part}.body`)}</p>
              </div>
            </li>
          ))}
        </ol>
        <div className="apf-language-pairs">
          {jobExamples.map((example) => {
            const businessModule = businessModuleById.get(example.workblockId);
            if (!businessModule?.jobStatementKey) return null;
            return (
              <div className="apf-language-pair" key={example.id}>
                <p className="apf-language-pair__term">{copy(businessModule.labelKey)}</p>
                <div>
                  <p className="apf-language-pair__label">{copy("page.language.label.worked")}</p>
                  <p className="apf-language-pair__line apf-language-pair__line--kept">{copy(businessModule.jobStatementKey)}</p>
                </div>
                <div>
                  <p className="apf-language-pair__label">{copy("page.language.label.broken")}</p>
                  <p className="apf-language-pair__line">{copy(`page.language.jobs.example.${example.id}.broken`)}</p>
                </div>
                <p className="apf-language-pair__note">
                  <span>{copy("page.language.label.why")}</span> {copy(`page.language.jobs.example.${example.id}.why`)}
                </p>
              </div>
            );
          })}
        </div>
      </section>
      ) : null}

      {showCraft ? (

      <section className="apf-page-section" id="mechanism">
        <SectionHeading title={heading("mechanism")} description={description("mechanism")} />
        <div className="apf-language-pairs">
          {mechanismPairs.map((pair) => (
            <div className="apf-language-pair" key={pair}>
              <div>
                <p className="apf-language-pair__label">{copy("page.language.mechanism.label.adjective")}</p>
                <p className="apf-language-pair__line">{copy(`page.language.mechanism.${pair}.adjective`)}</p>
              </div>
              <div>
                <p className="apf-language-pair__label">{copy("page.language.mechanism.label.mechanism")}</p>
                <p className="apf-language-pair__line apf-language-pair__line--kept">{copy(`page.language.mechanism.${pair}.mechanism`)}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
      ) : null}


      <section className="apf-page-section" id="vocabulary">
        <SectionHeading
          title={heading("vocabulary")}
          description={description("vocabulary")}
          action={
            <a className="apf-language-link" href="#glossary">
              <ArrowLinkLabel>{copy("page.language.vocabulary.action")}</ArrowLinkLabel>
            </a>
          }
        />
        <ol className="apf-ruled-index">
          {terms.map((term, index) => (
            <li className="apf-ruled-index__row" key={term}>
              <div className="apf-ruled-index__body">
                <span className="apf-ruled-index__ordinal">{ordinal(index + 1)}</span>
                <div className="apf-ruled-index__heading">
                  <strong className="apf-ruled-index__name">{copy(`page.language.term.${term}.label`)}</strong>
                </div>
                <p className="apf-ruled-index__description">{copy(`page.language.term.${term}.body`)}</p>
                <div className="apf-language-craft">
                  <div>
                    <p className="apf-language-craft__label">{copy("page.language.label.in-a-sentence")}</p>
                    <p className="apf-language-craft__line apf-language-craft__line--kept">{copy(`page.language.term.${term}.worked`)}</p>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="apf-page-section" id="terms">
        <SectionHeading title={heading("terms")} description={description("terms")} />
        <div className="apf-surface apf-data-table-wrap" role="region" aria-label={heading("terms")} tabIndex={0}>
          <table className="apf-data-table apf-data-table--responsive">
            <caption className="apf-visually-hidden">{heading("terms")}</caption>
            <thead>
              <tr>
                <th>{copy("page.language.terms.table.term")}</th>
                <th>{copy("page.language.terms.table.meaning")}</th>
                <th>{copy("page.language.terms.table.owner")}</th>
                {showCraft ? <th>{copy("page.language.terms.table.rule")}</th> : null}
              </tr>
            </thead>
            <tbody>
              {buyerTerms.map((term) => (
                <tr key={term}>
                  <td data-label={copy("page.language.terms.table.term")}>
                    <strong>{copy(`page.language.buyer-term.${term}.label`)}</strong>
                  </td>
                  <td data-label={copy("page.language.terms.table.meaning")}>{copy(`page.language.buyer-term.${term}.meaning`)}</td>
                  <td data-label={copy("page.language.terms.table.owner")}>{copy(`page.language.buyer-term.${term}.owner`)}</td>
                  {showCraft ? <td data-label={copy("page.language.terms.table.rule")}>{copy(`page.language.buyer-term.${term}.rule`)}</td> : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* The set's label sits in the craft block that owns it, so it keeps the
          * rhythm every other label on the page has instead of landing flush
          * against the table above it. */}
        {showCraft ? (
        <>
        <div className="apf-language-craft">
          <p className="apf-language-craft__label">{copy("page.language.terms.differentiators.label")}</p>
        </div>
        <div className="apf-language-pairs">
          {differentiators.map((differentiator) => (
            <div key={differentiator}>
              {workedPair(`page.language.differentiator.${differentiator}`, `page.language.differentiator.${differentiator}.term`)}
            </div>
          ))}
        </div>
        </>
        ) : null}
      </section>

      <section className="apf-page-section" id="refusals">
        <SectionHeading title={heading("refusals")} description={description("refusals")} />
        <div className="apf-ruled-columns">
          {refusals.map((refusal) => (
            <article className="apf-ruled-columns__item" key={refusal}>
              <p className="apf-ruled-columns__eyebrow">{copy(`page.language.refusal.${refusal}.eyebrow`)}</p>
              <h2>{copy(`page.language.refusal.${refusal}.label`)}</h2>
              <p>{copy(`page.language.refusal.${refusal}.body`)}</p>
            </article>
          ))}
        </div>
        {showCraft ? (
        <div className="apf-language-pairs">
          {refusals.map((refusal) => (
            <div key={refusal}>
              {workedPair(`page.language.refusal.${refusal}`, `page.language.refusal.${refusal}.eyebrow`)}
            </div>
          ))}
        </div>
        ) : null}
      </section>

      <section className="apf-page-section" id="patterns">
        <SectionHeading title={heading("patterns")} description={description("patterns")} />
        <ol className="apf-ruled-index">
          {patterns.map((pattern, index) => (
            <li className="apf-ruled-index__row" key={pattern}>
              <div className="apf-ruled-index__body">
                <span className="apf-ruled-index__ordinal">{ordinal(index + 1)}</span>
                <div className="apf-ruled-index__heading">
                  <strong className="apf-ruled-index__name">{copy(`page.language.pattern.${pattern}.label`)}</strong>
                </div>
                <p className="apf-ruled-index__description">{copy(`page.language.pattern.${pattern}.body`)}</p>
                {showCraft ? <div className="apf-language-pairs">{workedPair(`page.language.pattern.${pattern}`)}</div> : null}
              </div>
            </li>
          ))}
        </ol>
      </section>

      <GlossarySection />
      <Trail
        label={copy("trail.label", "Where to go next")}
        items={[
          { to: "/search", glyph: "utility.search", label: copy("trail.search"), context: copy("trail.search.context") },
          { to: "/reference", glyph: "class.record", label: copy("trail.reference"), context: copy("trail.reference.context") }
        ]}
      />
    </div>
  );
}
